
import { Link } from 'react-router-dom';

export default function PsychologicalSafetyPage() {
  const effects = [
    {
      title: '情報共有の促進',
      description: 'ミスや疑問を隠さずに共有できるため、問題の早期発見・早期解決が可能になる',
      icon: 'ri-share-line',
      examples: [
        '調剤ミスの報告がすぐに上がる',
        '分からないことを気軽に質問できる',
        '患者対応の困りごとを相談しやすい',
        '業務改善の提案が出やすい'
      ]
    },
    {
      title: '学習行動の加速',
      description: '失敗を恐れずに新しいことに挑戦できるため、チーム全体の学習速度が上がる',
      icon: 'ri-book-open-line',
      examples: [
        '新しい業務に積極的に取り組める',
        '失敗から学ぶ文化が根付く',
        '知識・経験の共有が活発になる',
        'スキルアップの機会が増える'
      ]
    },
    {
      title: 'エラーの早期発見',
      description: '小さな違和感や疑問を声に出せるため、重大なミスを未然に防げる',
      icon: 'ri-shield-check-line',
      examples: [
        '処方内容の疑義照会がしやすい',
        '調剤過程での気づきを共有できる',
        '患者情報の確認漏れを指摘しやすい',
        'ヒヤリハットの報告が増える'
      ]
    }
  ];

  const howToCreate = [
    {
      title: 'リーダーが脆弱性を開示する',
      description: '「分からない」「間違えた」「助けてほしい」をリーダー自身が言う',
      icon: 'ri-user-heart-line',
      practices: [
        'ミーティングで「私も分からないので、一緒に考えたい」と言う',
        '自分のミスを隠さずに共有する',
        'メンバーに助けを求める',
        '完璧を装わない'
      ]
    },
    {
      title: '質問・意見を歓迎する',
      description: '質問や反対意見を「貴重な情報」として扱い、感謝を伝える',
      icon: 'ri-question-answer-line',
      practices: [
        '質問に対して「いい質問ですね」と返す',
        '反対意見を「別の視点をありがとう」と受け止める',
        '沈黙を破った人に感謝する',
        '質問しやすい雰囲気をつくる'
      ]
    },
    {
      title: '失敗を学習の機会にする',
      description: '失敗を責めるのではなく、「何を学べるか」に焦点を当てる',
      icon: 'ri-lightbulb-line',
      practices: [
        'ヒヤリハットを「改善のチャンス」として扱う',
        '「なぜ起きたか」ではなく「どうすれば防げるか」を考える',
        '個人の責任追及ではなく、システム改善に焦点を当てる',
        '失敗から学んだことを共有する'
      ]
    },
    {
      title: '発言の偏りを是正する',
      description: '特定の人だけが話す状況を避け、全員が発言できる場をつくる',
      icon: 'ri-team-line',
      practices: [
        'ミーティングで発言していない人に意見を求める',
        '「他の人の意見も聞きたい」と促す',
        '少数意見を大切にする',
        '全員が発言する機会を設ける'
      ]
    }
  ];

  const measurements = [
    {
      question: 'チームでミスをすると、たいてい非難される',
      reverse: true
    },
    {
      question: 'チームのメンバーは、課題や難しい問題を指摘し合える',
      reverse: false
    },
    {
      question: 'チームのメンバーは、自分と異なるという理由で他者を拒絶することがある',
      reverse: true
    },
    {
      question: 'チームに対してリスクのある行動をしても安全である',
      reverse: false
    },
    {
      question: 'チームの他のメンバーに助けを求めることは難しい',
      reverse: true
    },
    {
      question: 'チームメンバーは誰も、自分の仕事を意図的におとしめるような行動をしない',
      reverse: false
    },
    {
      question: 'チームメンバーと仕事をするとき、自分のスキルと才能が尊重され、活かされていると感じる',
      reverse: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-rose-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-rose-600 transition-colors duration-200">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/team-building" className="hover:text-rose-600 transition-colors duration-200">チームビルディング</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-rose-600 font-medium">心理的安全性</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl mb-6 shadow-lg">
              <i className="ri-shield-check-line text-3xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              Ⅲ．心理的安全性（Psychological Safety）
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed mb-4">
              四大要素すべてを機能させる"土壌"
            </p>
            <div className="bg-rose-50 rounded-xl p-6 inline-block">
              <p className="text-lg text-gray-800 font-medium">
                ミス・疑問・反対意見を出しても不利益がないという共有認識
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Effects */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">心理的安全性がもたらす効果</h2>
            <div className="space-y-8">
              {effects.map((effect, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-rose-100">
                  <div className="flex items-start gap-6">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl shadow-md flex-shrink-0">
                      <i className={`${effect.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{effect.title}</h3>
                      <p className="text-gray-700 leading-relaxed mb-6">{effect.description}</p>
                      <div className="bg-rose-50 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-rose-900 mb-4 flex items-center gap-2">
                          <i className="ri-checkbox-circle-line"></i>
                          具体例
                        </h4>
                        <ul className="space-y-3">
                          {effect.examples.map((example, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-gray-700">
                              <i className="ri-arrow-right-s-line text-rose-600 mt-1 flex-shrink-0"></i>
                              <span>{example}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How to Create */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">心理的安全性を高める方法</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {howToCreate.map((method, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-rose-100">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg mb-4">
                    <i className={`${method.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{method.title}</h3>
                  <p className="text-gray-700 mb-4">{method.description}</p>
                  <div className="space-y-2">
                    {method.practices.map((practice, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-line text-rose-600 mt-0.5 flex-shrink-0"></i>
                        <span>{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Measurement */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-6 text-gray-900">心理的安全性の測定</h2>
            <p className="text-center text-gray-700 mb-12">
              以下の7つの質問で、チームの心理的安全性を測定できます。<br />
              各質問に対して、1（全くそう思わない）〜7（非常にそう思う）で評価してください。
            </p>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-rose-100">
              <ul className="space-y-4">
                {measurements.map((item, index) => (
                  <li key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <span className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full text-white font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-gray-800">{item.question}</p>
                      {item.reverse && (
                        <p className="text-xs text-rose-600 mt-1">※ 逆転項目（スコアが低いほど良い）</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-6 bg-rose-50 rounded-xl">
                <h4 className="font-bold text-rose-900 mb-2 flex items-center gap-2">
                  <i className="ri-information-line"></i>
                  評価方法
                </h4>
                <p className="text-sm text-gray-700">
                  逆転項目（1, 3, 5）は8から引いた値に変換し、全7項目の平均を算出します。平均が5以上であれば、心理的安全性が高いと言えます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Action Section */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
              <div className="flex items-start gap-6">
                <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-4">明日の一手</h2>
                  <p className="text-lg leading-relaxed mb-6">
                    次回のミーティングで、リーダー自身が「分からないこと」や「困っていること」を正直に話してみましょう。リーダーが脆弱性を開示することで、メンバーも安心して本音を話せるようになります。これが心理的安全性を高める第一歩です。
                  </p>
                  <div className="flex items-center gap-2 text-sm bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg inline-flex whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    <span>所要時間：5〜10分</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              <Link
                to="/team-building/four-elements"
                className="flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors duration-200"
              >
                <i className="ri-arrow-left-line"></i>
                <span>前へ：四大要素</span>
              </Link>
              <Link
                to="/team-building/leader-role"
                className="flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                <span>次へ：リーダーの役割</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
